import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  CalendarDays,
  PlusSquare,
  LogOut,
  ExternalLink,
  Boxes,
  Inbox,
  Wallet,
  Map,
  Package,
  BarChart3,
  Users,
  UserCog,
  Handshake,
  FileMinus,
  Star,
  ScrollText,
  ShieldCheck,
  Receipt,
  Search,
  Settings as SettingsIcon,
  Menu,
  X,
} from "lucide-react";
import { Logo } from "../../components/Logo";
import { NotificationsBell } from "../../components/admin/NotificationsBell";
import { GlobalSearchPalette, useGlobalSearchHotkey } from "../../components/admin/GlobalSearchPalette";
import { useAuth } from "../../lib/AuthContext";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/quotes", label: "Quote Inbox", icon: Inbox, testid: "nav-quotes" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/admin/clients", label: "Clients & Vehicles", icon: UserCog, testid: "nav-clients" },
  { to: "/admin/brokers", label: "Brokers", icon: Handshake, testid: "nav-brokers" },
  { to: "/admin/insurers", label: "Insurers", icon: ShieldCheck, testid: "nav-insurers" },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase, testid: "nav-jobs" },
  { to: "/admin/jobs/new", label: "New Job", icon: PlusSquare, testid: "nav-new-job" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarDays, testid: "nav-bookings" },
  { to: "/admin/routes", label: "Routes", icon: Map, testid: "nav-routes" },
  { to: "/admin/credit-notes", label: "Credit Notes", icon: FileMinus, testid: "nav-credit-notes" },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/admin/aging", label: "Outstanding", icon: Wallet, testid: "nav-aging" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star, testid: "nav-testimonials" },
  { to: "/admin/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/admin/stock", label: "Stock Requests", icon: Boxes, testid: "nav-stock" },
  { to: "/admin/performance", label: "Performance", icon: BarChart3, testid: "nav-performance" },
  { to: "/admin/audit-log", label: "Audit & Backups", icon: ScrollText, testid: "nav-audit" },
  { to: "/admin/awaiting-scheduling", label: "Awaiting Scheduling", icon: CalendarDays, testid: "nav-awaiting" },
  { to: "/admin/schedule-changes", label: "Schedule Changes", icon: CalendarDays, testid: "nav-schedule-changes" },
  { to: "/admin/completion-queue", label: "Completion Queue", icon: ScrollText, testid: "nav-completion-queue" },
  { to: "/admin/invoice-audit", label: "Invoice Audit", icon: Receipt, testid: "nav-invoice-audit" },
  { to: "/admin/users", label: "Users & Access", icon: ShieldCheck, testid: "nav-users" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const SidebarBody = ({ onNavigate, onLogout }) => (
  <>
    <div className="px-5 py-6 border-b border-white/10">
      <Logo variant="group" size="sm" dark />
      <div className="mt-3 font-display uppercase tracking-widest text-xs text-[#ffc438]">
        Admin Portal
      </div>
    </div>
    <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1" data-testid="admin-nav">
      {navItems.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md transition font-display uppercase tracking-wider text-sm ${
                isActive
                  ? "bg-[#f28a16] text-white shadow"
                  : "text-white/80 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon size={18} />
            {it.label}
          </NavLink>
        );
      })}
    </nav>
    <div className="px-3 py-4 border-t border-white/10 space-y-1">
      <Link
        to="/"
        data-testid="admin-back-to-site"
        onClick={onNavigate}
        className="flex items-center gap-3 px-3 py-2 rounded-md text-white/70 hover:bg-white/10 hover:text-white transition font-display uppercase tracking-wider text-xs"
      >
        <ExternalLink size={14} /> View website
      </Link>
      <button
        type="button"
        data-testid="admin-logout"
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-white/70 hover:bg-white/10 hover:text-white transition font-display uppercase tracking-wider text-xs"
      >
        <LogOut size={14} /> Sign out
      </button>
    </div>
  </>
);

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useGlobalSearchHotkey(setSearchOpen);

  // Close mobile drawer whenever the route changes so navigation feels native.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#f5f6f6] text-[#111619] flex" data-testid="admin-layout">
      <GlobalSearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-[#20282c] text-white sticky top-0 h-screen flex-col">
        <SidebarBody onLogout={onLogout} />
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-mobile-backdrop"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[82vw] max-w-xs bg-[#20282c] text-white flex flex-col shadow-2xl transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-mobile-drawer"
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="absolute right-3 top-3 h-9 w-9 rounded-md text-white/80 hover:bg-white/10 flex items-center justify-center"
          data-testid="admin-mobile-close"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>
        <SidebarBody onNavigate={() => setMobileOpen(false)} onLogout={onLogout} />
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-[#cfd7db] bg-white sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden h-9 w-9 rounded-md border border-[#cfd7db] text-[#3f4b52] flex items-center justify-center hover:bg-[#f5f6f6]"
                data-testid="admin-mobile-menu-btn"
                aria-label="Open menu"
              >
                <Menu size={18} />
              </button>
              <div className="font-display uppercase tracking-widest text-[11px] sm:text-xs text-[#3f4b52] truncate">
                Diesel <span className="text-[#f28a16]">&</span> Dust · Admin
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                data-testid="admin-search-btn"
                className="hidden sm:inline-flex items-center gap-2 px-3 h-9 border border-[#cfd7db] rounded-md text-xs text-[#3f4b52] hover:bg-[#f5f6f6]"
              >
                <Search size={14} /> Search
                <kbd className="ml-2 font-mono text-[10px] px-1.5 py-0.5 bg-[#f5f6f6] border border-[#cfd7db] rounded">
                  ⌘K
                </kbd>
              </button>
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                data-testid="admin-search-btn-mobile"
                aria-label="Search"
                className="sm:hidden h-9 w-9 rounded-md border border-[#cfd7db] text-[#3f4b52] flex items-center justify-center hover:bg-[#f5f6f6]"
              >
                <Search size={16} />
              </button>
              <span className="text-xs text-[#3f4b52] hidden md:inline max-w-[180px] truncate" data-testid="admin-user-email">{user?.email}</span>
              <NotificationsBell />
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
          {user?.must_change_password && location.pathname !== "/admin/change-password" && (
            <div className="mb-4 rounded-md border border-[#f28a16] bg-[#fff7ed] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="must-change-password-banner">
              <div className="text-sm text-[#7c2d12]">
                <ShieldCheck size={14} className="inline mr-1 text-[#f28a16]" />
                You're using a temporary password. Please set a new one now.
              </div>
              <NavLink
                to="/admin/change-password"
                className="bg-[#f28a16] text-white px-4 h-9 rounded-md inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs hover:brightness-110"
                data-testid="must-change-password-cta"
              >
                Change password →
              </NavLink>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
